// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable from "@insite/mobius/Clickable";
import LazyImage from "@insite/mobius/LazyImage";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import TitleTypography from "@stanion/App/Components/TitleTypography/TitleTypography";
import XngCarousel from "@stanion/App/Components/XngCarousel/XngCarousel";
import loadFeaturedCategories from "@stanion/App/Handlers/Categories/LoadFeaturedCategories";
import { getFeaturedCategories } from "@stanion/App/Store/Selectors/CategoriesSelector";
import { XNGAGE_CUSTOM_WIDGET } from "@stanion/XDL/constants";
import { xScreen } from "@stanion/XDL/Utilities";
import React, { FC, useEffect } from "react";
import { useDispatch } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
    link = "link",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.link]: LinkFieldValue;
    };
}

export interface FeaturedCategoriesStyles {
    wrapper?: InjectableCss;
    carouselSlidesContainer?: InjectableCss;
    slideWrapper?: InjectableCss;
    titleText?: TypographyProps;
    image?: InjectableCss;
}

type Props = OwnProps;

export const featuredCategoriesStyles: FeaturedCategoriesStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    carouselSlidesContainer: {
        css: css`
            max-width: 100%;
            .swiper-slide {
                max-width: 204px;

                ${xScreen("lte", "sm")} {
                    max-width: 160px;
                }
                ${xScreen("lte", 375)} {
                    max-width: 100%;
                }
            }
        `,
    },
    slideWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            max-width: 150px;

            ${xScreen("lte", 375)} {
                max-width: 100%;
            }
        `,
    },
    titleText: {
        variant: "h6",
        as: "h3",
        css: css`
            margin-top: 16px;
            font-weight: bold;
            text-align: center;
        `,
    },
    image: {
        css: css`
            width: 204px;
            height: 204px;
            object-fit: contain;
            ${xScreen("lte", "md")} {
                width: 168px;
            }
            ${xScreen("lte", "sm")} {
                width: 140px;
                height: 140px;
            }
        `,
    },
};

const styles = featuredCategoriesStyles;
const FeaturedCategories: FC<Props> = ({ fields }) => {
    const dispatch = useDispatch();
    const { featuredCategories } = useAppSelector(state => {
        return {
            featuredCategories: getFeaturedCategories(state),
        };
    });
    const allProductPageUrl = useGetLink(fields.link);

    useEffect(() => {
        dispatch(loadFeaturedCategories({}));
    }, []);

    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="featuredCategoriesWidget">
            <TitleTypography
                linkText={translate("View All Categories")}
                link={allProductPageUrl.url}
                data-test-selector="featuredCategoriesTitle"
            >
                {translate(fields.title) || translate("Featured Categories")}
            </TitleTypography>
            <StyledWrapper {...styles.carouselSlidesContainer} data-test-selector="slides">
                <XngCarousel xSlidesPerView={true} spaceBetween={22}>
                    {featuredCategories?.map((item: any) => (
                        <StyledWrapper key={item.id}>
                            <Clickable href={item.path} {...styles.slideWrapper}>
                                <LazyImage
                                    {...styles.image}
                                    src={item.smallImagePath}
                                    altText={`${item.imageAltText}`}
                                />
                                <Typography {...styles.titleText}>{item.shortDescription}</Typography>
                            </Clickable>
                        </StyledWrapper>
                    ))}
                </XngCarousel>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: FeaturedCategories,
    definition: {
        group: XNGAGE_CUSTOM_WIDGET,
        icon: "link",
        isSystem: true,
        fieldDefinitions: [
            {
                name: fields.title,
                editorTemplate: "TextField",
                defaultValue: "",
                displayName: "Widget Title",
                fieldType: "General",
                isRequired: false,
            },
            {
                name: fields.link,
                displayName: "All Product Page Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                isRequired: true,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
